import { useCallback, useEffect, useMemo, useState } from "react";
import useEmblaCarousel from "embla-carousel-react";
import { motion } from "framer-motion";
import axios from "axios";
import {
  Star,
  Quote,
  ChevronLeft,
  ChevronRight,
  ExternalLink,
  Loader2,
  PenSquare,
} from "lucide-react";
import {
  CornerHeadphones,
  LightLeak,
  AmbientDust,
  ScrubLine,
  SpinningRecord,
  HugeWatermarkText,
  CircularText,
} from "@/components/landing/AmbientMotion";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const GOOGLE_REVIEWS_URL = "https://g.page/r/CepB5VHvhgkqEAE/review";

const ACCENTS = ["#00f0ff", "#ff0055", "#a395c3", "#6b21d9"];

const initialsFrom = (name) =>
  (name || "Google user")
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((s) => s[0])
    .join("")
    .toUpperCase();

const Testimonials = () => {
  const [reviews, setReviews] = useState([]);
  const [placeRating, setPlaceRating] = useState(5);
  const [reviewCount, setReviewCount] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState({});

  const toggleExpand = (i) =>
    setExpanded((prev) => ({ ...prev, [i]: !prev[i] }));

  const COLLAPSED_CHARS = 180;

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/google-reviews`, {
          timeout: 12000,
        });
        if (cancelled) return;
        setReviews(Array.isArray(data?.reviews) ? data.reviews : []);
        if (data?.place_rating != null) setPlaceRating(data.place_rating);
        if (data?.user_rating_count != null)
          setReviewCount(data.user_rating_count);
      } catch {
        if (!cancelled) setReviews([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const decorated = useMemo(
    () =>
      reviews.map((r, i) => ({
        ...r,
        accent: ACCENTS[i % ACCENTS.length],
        initials: initialsFrom(r.author_name),
      })),
    [reviews],
  );

  const [emblaRef, emblaApi] = useEmblaCarousel({
    loop: true,
    align: "start",
    skipSnaps: false,
    containScroll: "trimSnaps",
  });
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [scrollSnaps, setScrollSnaps] = useState([]);

  const scrollPrev = useCallback(
    () => emblaApi && emblaApi.scrollPrev(),
    [emblaApi],
  );
  const scrollNext = useCallback(
    () => emblaApi && emblaApi.scrollNext(),
    [emblaApi],
  );

  useEffect(() => {
    if (!emblaApi) return;
    const onSelect = () => setSelectedIndex(emblaApi.selectedScrollSnap());
    const syncSnaps = () => setScrollSnaps(emblaApi.scrollSnapList());
    emblaApi.on("select", onSelect);
    emblaApi.on("reInit", syncSnaps);
    onSelect();
    const t = setTimeout(syncSnaps, 0);
    // Auto-advance is a major cause of repaint flicker on mobile/tablet
    // (each tick forces a re-layout of every card, and the section's
    // decorative auras re-composite). Only auto-advance on desktop.
    const isDesktop =
      typeof window !== "undefined" &&
      window.matchMedia("(min-width: 1024px)").matches;
    const interval = isDesktop
      ? setInterval(() => {
          if (emblaApi) emblaApi.scrollNext();
        }, 5500)
      : null;
    return () => {
      clearTimeout(t);
      if (interval) clearInterval(interval);
      emblaApi.off("select", onSelect);
      emblaApi.off("reInit", syncSnaps);
    };
  }, [emblaApi, decorated.length]);

  return (
    <section
      id="testimonials"
      data-testid="testimonials-section"
      className="relative py-16 md:py-24"
    >
      <div className="absolute inset-0 -z-10">
        <div className="absolute top-[10%] right-[20%] w-[520px] h-[520px] rounded-full bg-[#6b21d9]/15 blur-[160px]" />
      </div>
      <CornerHeadphones color="#a78bfa" corner="tl" size={44} />
      <LightLeak color="#ffcc44" corner="br" size={280} />
      <AmbientDust count={18} color="#a78bfa" />
      <HugeWatermarkText text="5.0" corner="bl" color="#a78bfa" size={280} />
      <div className="absolute top-24 left-8 pointer-events-none">
        <CircularText text="TRUSTED · REVIEWED · CERTIFIED · " size={160} color="#a78bfa" duration={45} />
      </div>
      <div className="absolute top-1/2 -right-16 -translate-y-1/2 pointer-events-none opacity-40">
        <SpinningRecord size={280} color="#a78bfa" speed={60} />
      </div>

      <div className="max-w-[1400px] mx-auto px-5 md:px-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7 }}
          className="grid grid-cols-12 gap-6 items-end mb-12"
        >
          <div className="col-span-12 md:col-span-7">
            <div className="text-[11px] uppercase tracking-[0.32em] text-[#00f0ff] mb-5">
              [ Reviews · Google ]
            </div>
            <h2
              data-testid="testimonials-heading"
              className="font-display font-black tracking-tighter text-[40px] md:text-[56px] lg:text-[68px] leading-[0.95]"
            >
              <span className="text-white">
                {placeRating?.toFixed
                  ? placeRating.toFixed(1)
                  : Number(placeRating || 5).toFixed(1)}{" "}
                stars,
              </span>{" "}
              <span className="italic font-light text-[#a395c3]">
                straight from
              </span>{" "}
              <span className="text-[#00f0ff] text-glow-cyan">creators.</span>
            </h2>
          </div>
          <div className="col-span-12 md:col-span-5 flex flex-col md:items-end gap-4">
            <a
              href={GOOGLE_REVIEWS_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="testimonials-verified-badge"
              className="inline-flex items-center gap-3 px-4 py-2.5 rounded-full bg-white/[0.04] border border-white/10 backdrop-blur-md hover:border-[#ffb800]/50 hover:bg-white/[0.08] transition-all group"
            >
              <div className="flex -space-x-1">
                {[1, 2, 3, 4, 5].map((i) => (
                  <Star
                    key={i}
                    size={14}
                    className="fill-[#ffb800] text-[#ffb800]"
                  />
                ))}
              </div>
              <span className="text-[12px] text-white/80 font-medium group-hover:text-white transition-colors">
                {placeRating?.toFixed ? placeRating.toFixed(1) : "5.0"} average ·{" "}
                {reviewCount != null ? `${reviewCount} verified` : "verified"}{" "}
                Google
              </span>
              <ExternalLink
                size={11}
                className="text-white/50 group-hover:text-white transition-colors"
              />
            </a>
            <div className="flex items-center gap-3">
              <a
                href={GOOGLE_REVIEWS_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="testimonials-leave-review"
                className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-[#00f0ff] text-[#05050a] font-display font-semibold text-[13px] hover:bg-white transition-colors shadow-[0_0_28px_rgba(0,240,255,0.25)]"
              >
                <PenSquare size={14} />
                Leave us a review
              </a>
              <a
                href={GOOGLE_REVIEWS_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="testimonials-see-all"
                className="inline-flex items-center gap-1.5 text-sm text-white/70 hover:text-[#00f0ff] transition-colors"
              >
                See all <ExternalLink size={13} />
              </a>
            </div>
          </div>
        </motion.div>

        {/* Carousel */}
        <div className="relative">
          {loading ? (
            <div className="flex items-center justify-center py-20 text-white/40">
              <Loader2 className="animate-spin" />
            </div>
          ) : decorated.length === 0 ? (
            <div className="rounded-3xl p-10 text-center text-[#a395c3] bg-white/[0.03] border border-white/10">
              Reviews are loading. Refresh in a moment, or{" "}
              <a
                href={GOOGLE_REVIEWS_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#00f0ff] underline"
              >
                read them on Google
              </a>
              .
            </div>
          ) : (
            <>
              <div
                className="overflow-hidden"
                ref={emblaRef}
                data-testid="testimonials-carousel"
              >
                <div className="flex gap-5 py-4 items-stretch">
                  {decorated.map((r, i) => {
                    const fullText = r.text || "";
                    const isLong = fullText.length > COLLAPSED_CHARS;
                    const isOpen = !!expanded[i];
                    const shown =
                      isLong && !isOpen
                        ? fullText.slice(0, COLLAPSED_CHARS).trimEnd() + "…"
                        : fullText;
                    return (
                      <div
                        key={(r.author_name || "r") + i}
                        className="flex-[0_0_88%] sm:flex-[0_0_46%] lg:flex-[0_0_31%]"
                        data-testid={`testimonial-card-${i}`}
                      >
                        <article className="relative h-full min-h-[280px] flex flex-col rounded-3xl bg-[#11091a]/70 backdrop-blur-md border border-white/10 p-6 md:p-7 hover:border-white/20 transition-all overflow-hidden group">
                          <div
                            className="absolute -top-20 -right-20 w-56 h-56 rounded-full blur-3xl opacity-40 group-hover:opacity-60 transition-opacity pointer-events-none"
                            style={{ background: r.accent }}
                          />
                          <div className="relative flex items-start justify-between mb-4">
                            <Quote
                              size={28}
                              className="text-white/15"
                              strokeWidth={1.5}
                            />
                            <div className="flex items-center gap-0.5">
                              {Array.from({ length: r.rating || 5 }).map(
                                (_, k) => (
                                  <Star
                                    key={k}
                                    size={13}
                                    className="fill-[#ffb800] text-[#ffb800]"
                                  />
                                ),
                              )}
                            </div>
                          </div>
                          <p className="relative font-display text-[15px] md:text-base leading-snug text-white/90 flex-1">
                            {shown}
                          </p>
                          {isLong && (
                            <button
                              type="button"
                              onClick={() => toggleExpand(i)}
                              data-testid={`testimonial-see-more-${i}`}
                              className="relative mt-3 self-start text-[11px] uppercase tracking-[0.22em] font-semibold text-[#00f0ff] hover:text-white transition-colors"
                            >
                              {isOpen ? "Show less" : "See more"}
                            </button>
                          )}
                          <div className="relative mt-5 pt-4 border-t border-white/10 flex items-center gap-3">
                            {r.author_photo ? (
                              <img
                                src={
                                  // Google's `lh3.googleusercontent.com` accepts a
                                  // `=sNN` size suffix — swap the default s128
                                  // down to s64 (2x for a 32px display) so we
                                  // don't ship 30 KB per avatar for a 34-px thumb.
                                  r.author_photo.includes("googleusercontent.com")
                                    ? r.author_photo.replace(/=s\d+(-c[^-]*)?(-[a-z0-9]+)?(-[a-z0-9]+)?$/i, "=s64-c")
                                    : r.author_photo
                                }
                                alt={r.author_name}
                                loading="lazy"
                                decoding="async"
                                width={36}
                                height={36}
                                referrerPolicy="no-referrer"
                                className="w-9 h-9 rounded-full object-cover border border-white/10 flex-shrink-0"
                              />
                            ) : (
                              <div
                                className="w-9 h-9 rounded-full flex items-center justify-center font-display font-bold text-[11px] flex-shrink-0"
                                style={{
                                  background: `linear-gradient(135deg, ${r.accent}40, transparent)`,
                                  color: r.accent,
                                  border: `1px solid ${r.accent}50`,
                                }}
                              >
                                {r.initials}
                              </div>
                            )}
                            <div className="leading-tight min-w-0 flex-1">
                              <div className="font-display font-semibold text-[13px] truncate">
                                {r.author_name}
                              </div>
                              <div className="text-[10px] text-[#a395c3] mt-0.5">
                                {r.relative_time || "Verified Google review"}
                              </div>
                            </div>
                            <a
                              href={r.author_url || GOOGLE_REVIEWS_URL}
                              target="_blank"
                              rel="noopener noreferrer"
                              aria-label="View on Google"
                              className="ml-auto opacity-70 hover:opacity-100 transition-opacity flex-shrink-0"
                            >
                              <svg
                                viewBox="0 0 48 48"
                                className="w-4 h-4"
                                aria-hidden="true"
                              >
                                <path
                                  fill="#FFC107"
                                  d="M43.6 20.5H42V20H24v8h11.3c-1.6 4.7-6.1 8-11.3 8-6.6 0-12-5.4-12-12s5.4-12 12-12c3 0 5.8 1.1 7.9 3l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.4-.4-3.5z"
                                />
                                <path
                                  fill="#FF3D00"
                                  d="M6.3 14.7l6.6 4.8C14.6 16 18.9 13 24 13c3 0 5.8 1.1 7.9 3l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z"
                                />
                                <path
                                  fill="#4CAF50"
                                  d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2c-2 1.4-4.5 2.4-7.2 2.4-5.2 0-9.6-3.3-11.3-8l-6.5 5C9.5 39.6 16.2 44 24 44z"
                                />
                                <path
                                  fill="#1976D2"
                                  d="M43.6 20.5H42V20H24v8h11.3c-.8 2.3-2.3 4.3-4.1 5.6l6.2 5.2C40.9 35.7 44 30.3 44 24c0-1.3-.1-2.4-.4-3.5z"
                                />
                              </svg>
                            </a>
                          </div>
                        </article>
                      </div>
                    );
                  })}
                </div>
              </div>

              <div className="mt-8 flex items-center justify-between gap-4">
                <div
                  className="flex items-center gap-1.5"
                  data-testid="testimonials-dots"
                >
                  {scrollSnaps.map((_, i) => (
                    <button
                      key={i}
                      onClick={() => emblaApi && emblaApi.scrollTo(i)}
                      data-testid={`testimonials-dot-${i}`}
                      aria-label={`Go to review ${i + 1}`}
                      className={`h-1.5 rounded-full transition-all ${
                        selectedIndex === i
                          ? "w-8 bg-[#00f0ff]"
                          : "w-1.5 bg-white/20 hover:bg-white/40"
                      }`}
                    />
                  ))}
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={scrollPrev}
                    aria-label="Previous review"
                    data-testid="testimonials-prev"
                    className="w-11 h-11 rounded-full bg-white/[0.04] border border-white/10 flex items-center justify-center hover:bg-white/[0.1] hover:border-white/20 transition-all"
                  >
                    <ChevronLeft size={18} />
                  </button>
                  <button
                    onClick={scrollNext}
                    aria-label="Next review"
                    data-testid="testimonials-next"
                    className="w-11 h-11 rounded-full bg-[#00f0ff] text-[#05050a] flex items-center justify-center hover:bg-white transition-colors"
                  >
                    <ChevronRight size={18} />
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </section>
  );
};

export default Testimonials;
